import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Button, Heading, SpinningLoader, Tabs, Text } from '@frontend/design-system';
import { LocationServiceLocation, LocationServiceLocationItem, LocationServiceRecentLocation } from '../../types';
import { LocationSearchGrid } from './location-search-grid';

const styles = {
  tray: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxHeight: '100%',
    height: '100%',
    background: theme.colors.white,
    padding: theme.spacing(3),
    overflow: 'hidden',
  }),
  head: css({
    padding: `0 ${theme.spacing(3)}`,
    margin: `0 -${theme.spacing(2)}`,
    alignItems: 'center',

    '.location-details__close-btn': {
      marginLeft: 'auto',
    },
  }),
  title: css({
    display: 'flex',
    gap: theme.spacing(2),
  }),
  loadingWrapper: css({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  }),
  pinnedGridStyles: css({
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  }),
};

export type LocationHistoryTrayProps = {
  location: LocationServiceLocation | null;
  pinnedLocations: LocationServiceRecentLocation[];
  recentLocations: LocationServiceRecentLocation[];

  onClose: () => void;
  onChangeLocation: (location: LocationServiceLocationItem) => void;
  onRemovePinnedLocation: (locationId: string) => void;
  onRemoveRecentLocation: (locationId: string) => void;
};
export const LocationHistoryTray = ({
  location,
  pinnedLocations,
  recentLocations,
  onClose,
  onChangeLocation,
  onRemovePinnedLocation,
  onRemoveRecentLocation,
}: LocationHistoryTrayProps) => {
  const [view, setView] = useState<'pinned' | 'recent'>('pinned');

  const heading = useMemo(() => {
    return {
      pinned: 'Pinned Locations',
      recent: 'Recent Locations',
    }[view];
  }, [view]);

  const lead = useMemo(() => {
    return {
      pinned: 'These are the locations that you and other members of your department have pinned.',
      recent: 'These are the locations that you have recently visited.',
    }[view];
  }, [view]);

  return (
    <div className='location-details__tray' css={styles.tray}>
      <div className='location-history__head' css={styles.head}>
        <div className='location-history__title' css={styles.title}>
          <div>
            <Heading level={2}>{heading}</Heading>
          </div>
          <div className='location-details__close-btn'>
            <Button iconName='x' size='large' variant='secondary' onClick={onClose} />
          </div>
        </div>

        <Text size='large' color='subdued'>
          {lead}
        </Text>
      </div>
      {!location && (
        <div css={styles.loadingWrapper}>
          <SpinningLoader size='large' />
        </div>
      )}

      <Tabs controlledTab={view} onChange={(id) => setView(id as typeof view)}>
        <Tabs.Bar fullWidth={false}>
          <Tabs.Tab id='pinned'>Pinned Locations</Tabs.Tab>
          <Tabs.Tab id='recent'>Recently Visited</Tabs.Tab>
        </Tabs.Bar>
        <Tabs.Panel controller='pinned'>
          <LocationSearchGrid
            gridStyles={styles.pinnedGridStyles}
            locations={pinnedLocations}
            onChangeLocation={onChangeLocation}
            onRemoveLocation={onRemovePinnedLocation}
          />
        </Tabs.Panel>
        <Tabs.Panel controller='recent'>
          <LocationSearchGrid
            locations={recentLocations}
            onChangeLocation={onChangeLocation}
            onRemoveLocation={onRemoveRecentLocation}
          />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};
